import { centsToDollars, formatCentsToDollars } from '../money';
import JSZip from 'jszip';
import { jsPDFWithPlugin } from 'src/app/private/home/components/all-files-list/all-files-list.component';
import jsPDF from 'jspdf';
import { SolunoActivityCode } from '@models/soluno';
import { format } from 'date-fns';
import { StatementOfAccountTotals } from '@services/financialTotals/financial-totals.service';
import { formatPercent } from '../number';
import { removeIllegalCharactersFromFileName } from '../documents';
import { SolunoAccountingItem } from 'src/app/private/sale/accounting/accounting.models';

export const getSolunoDisbursementCsvBlob = (
	data: SolunoAccountingItem[],
	accountingNumber: string,
): Blob => {
	const date = format(new Date(), 'MM-dd-yyyy');
	const columnTitles = [
		'ACTIVITY',
		'AMOUNT',
		'CLIENT',
		'date mdy',
		'ENTRY',
		'EXPLANATION',
		'FILE',
		'GL',
		'PAID TO',
		'REF',
		'SRC',
		'TAX',
	];
	const csvData = [
		columnTitles,
		...data.map((item) => [
			item?.activityCode?.id,
			centsToDollars(item.amount),
			'',
			date,
			'',
			'',
			accountingNumber,
			item?.activityCode?.GLAccount?.id,
			'',
			'',
			'',
			item.hst ? 'GST/HST only' : 'Exempt',
		]),
	];

	const csv = csvData.map((row) => row.join(','));

	const csvArray = csv.join('\r\n');

	const blob = new Blob([csvArray], { type: 'data:text/csv;charset=utf-8,' });

	return blob;
};

export const getSolunoFeeCsvBlob = (
	data: SolunoAccountingItem[],
	accountingNumber: string,
	lawyerName: string,
): Blob => {
	const date = format(new Date(), 'MM-dd-yyyy');
	const columnTitles = [
		'Date MDY',
		'File',
		'Fee Earner',
		'Task',
		'Hours',
		'Rate',
		'Amount',
		'Activity Code',
		'Explanation',
	];
	const csvData = [
		columnTitles,
		...data.map((item) => [
			date,
			accountingNumber,
			lawyerName,
			'',
			'',
			'',
			centsToDollars(item.amount),
			item?.activityCode?.id,
			'',
		]),
	];

	const csv = csvData.map((row) => row.join(','));

	const csvArray = csv.join('\r\n');

	const blob = new Blob([csvArray], { type: 'data:text/csv;charset=utf-8,' });

	return blob;
};

export const solunoExport = async (
	fees: SolunoAccountingItem[],
	disbursements: SolunoAccountingItem[],
	accountingNumber: string,
	matterName: string,
	lawyerName: string,
) => {
	const feesBlob = getSolunoFeeCsvBlob(fees, accountingNumber, lawyerName);
	const disbursementsBlob = getSolunoDisbursementCsvBlob(
		disbursements,
		accountingNumber,
	);
	const date = format(new Date(), 'MM-dd-yyyy');

	accountingNumber = removeIllegalCharactersFromFileName(accountingNumber);
	matterName = removeIllegalCharactersFromFileName(matterName);

	const zip = new JSZip();
	zip.file(`Fees_${accountingNumber}_${matterName}_${date}.csv`, feesBlob);
	zip.file(
		`Disbursements_${accountingNumber}_${matterName}_${date}.csv`,
		disbursementsBlob,
	);

	const a = document.createElement('a');
	const url = window.URL.createObjectURL(
		await zip.generateAsync({ type: 'blob' }),
	);

	a.href = url;
	a.download = `${accountingNumber}_${matterName}_${date}.zip`;
	a.click();
	window.URL.revokeObjectURL(url);
	a.remove();
};

export const solunoPrint = (
	hstItems: SolunoAccountingItem[],
	nonHstItems: SolunoAccountingItem[],
	convenyancingFeesItems: SolunoAccountingItem[],
	statementOfAccountTotals: StatementOfAccountTotals,
	hst: number,
	accountingNumber: string,
	fileNumber: string,
	fileName: string,
) => {
	const doc = new jsPDF({
		orientation: 'portrait',
		unit: 'mm',
	}) as jsPDFWithPlugin;

	doc.setFontSize(10);
	doc.setFont(undefined, 'bold');
	doc.text(`Date: ${new Date().toLocaleDateString('en-US')}`, 14, 10);
	doc.text(`File Number: ${fileNumber}`, 14, 14);
	doc.text(`File Name: ${fileName}`, 14, 18);
	doc.text(`Accounting Number: ${accountingNumber}`, 14, 22);

	const columnStyles = {
		0: { cellWidth: 80 },
		1: { cellWidth: 30 },
		2: { cellWidth: 80, halign: 'right' },
	};

	const headStyles = {
		fillColor: '044321',
	};

	const {
		feesHST,
		feesTotalWithHST,
		HSTDisbursementsHST,
		HSTDisbursementsTotal,
		nonHSTDisbursementsTotal,
	} = statementOfAccountTotals;

	const convenyancingFeesBodyItems = [
		...convenyancingFeesItems.map((item) => [
			item.title,
			`${formatCentsToDollars(item.amount)}`,
			`${getSolunoActivityCodeLabelDescription(item?.activityCode)}`,
		]),
		[
			'\n Total',
			' ',
			`HST (${formatPercent(hst)}%) ${formatCentsToDollars(feesHST)}\n${formatCentsToDollars(feesTotalWithHST)}`,
		],
	];

	const convenyancingFeesHeaders = [
		`Legal Fees (${convenyancingFeesItems.length})`,
		'Amount',
		'Activity Code',
	];

	doc.autoTable({
		startY: 26,
		head: [convenyancingFeesHeaders] as any,
		body: convenyancingFeesBodyItems,
		columnStyles: columnStyles as any,
		headStyles: headStyles as any,
		didParseCell: function (data) {
			const { cell, column } = data;
			if (
				convenyancingFeesHeaders.includes(cell.raw as string) &&
				column.index === convenyancingFeesHeaders.length - 1
			) {
				cell.styles.halign = 'right';
			}

			if (data.row.index === convenyancingFeesBodyItems.length - 1) {
				data.cell.styles.fontStyle = 'bold';
			}
		},
	});

	const hstDisbursementesBodyItems = [
		...hstItems.map((item) => [
			item.title,
			`${formatCentsToDollars(item.amount)}`,
			`${getSolunoActivityCodeLabelDescription(item?.activityCode)}`,
		]),
		[
			'\n Total',
			' ',
			`HST (${formatPercent(hst)}%) ${formatCentsToDollars(HSTDisbursementsHST)}\n${formatCentsToDollars(HSTDisbursementsTotal + HSTDisbursementsHST)}`,
		],
	];

	const hstDisbursementsHeaders = [
		`HST Disbursements (${hstItems.length})`,
		'Amount',
		'Activity Code & G/L Account',
	];

	doc.autoTable({
		startY: (doc as any).lastAutoTable.finalY + 10,
		head: [hstDisbursementsHeaders] as any,
		body: hstDisbursementesBodyItems,
		columnStyles: columnStyles as any,
		headStyles: headStyles as any,
		didParseCell: function (data) {
			const { cell, column } = data;
			if (
				hstDisbursementsHeaders.includes(cell.raw as string) &&
				column.index === convenyancingFeesHeaders.length - 1
			) {
				cell.styles.halign = 'right';
			}

			if (data.row.index === hstDisbursementesBodyItems.length - 1) {
				data.cell.styles.fontStyle = 'bold';
			}
		},
	});

	const nonHstDisbursementesBodyItems = [
		...nonHstItems.map((item) => [
			item.title,
			`${formatCentsToDollars(item.amount)}`,
			`${getSolunoActivityCodeLabelDescription(item?.activityCode)}`,
		]),
		['Total', '', `${formatCentsToDollars(nonHSTDisbursementsTotal)}`],
	];

	const nonHstDisbursementsHeaders = [
		`Non HST Disbursements (${nonHstItems.length})`,
		'Amount',
		'Activity code & G/L Account',
	];

	doc.autoTable({
		startY: (doc as any).lastAutoTable.finalY + 10,
		head: [nonHstDisbursementsHeaders] as any,
		body: nonHstDisbursementesBodyItems,
		columnStyles: columnStyles as any,
		headStyles: headStyles as any,
		didParseCell: function (data) {
			const { cell, column } = data;
			if (
				nonHstDisbursementsHeaders.includes(cell.raw as string) &&
				column.index === convenyancingFeesHeaders.length - 1
			) {
				cell.styles.halign = 'right';
			}

			if (data.row.index === nonHstDisbursementesBodyItems.length - 1) {
				data.cell.styles.fontStyle = 'bold';
			}
		},
	});
	doc.autoPrint();

	const url = doc.output('bloburl').toString();

	return url;
};

export const getSolunoActivityCodeLabelDescription = (
	activityCode: SolunoActivityCode,
) => {
	if (!activityCode) {
		return '';
	}

	const details = [activityCode.id];

	if (activityCode.GLAccount) {
		details.push(activityCode.GLAccount.id);
	}
	return `${activityCode.description} (${details.join(' - ')})`;
};
